/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type UserUpdateProfileRequest } from '@adp-studio/api-schema/passport';
import { passport } from '@adp-studio/api-schema';
import { resetUserStore, type UserInfo } from '@adp-foundation/account-base';

export const passportApi = {
  checkLogin: async () => {
    const res = (await passport.PassportAccountInfoV2({})) as unknown as {
      data: UserInfo;
    };
    return res.data;
  },

  logout: async () => {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('adp-auth-lifecycle-v1');
      channel.postMessage({ type: 'invalidate', pendingLogout: true });
      channel.close();
    }
    await passport.PassportWebLogoutGet(
      { next: '/' },
      { headers: { 'X-ADP-CSRF': '1' } },
    );
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('adp-auth-lifecycle-v1');
      channel.postMessage({ type: 'invalidate', pendingLogout: false });
      channel.close();
    }
  },

  uploadAvatar: async ({ avatar }: { avatar: File }) => {
    const res = await passport.UserUpdateAvatar({
      avatar,
    });

    return res.data;
  },

  updatePassword: async (params: { password: string; email: string }) => {
    await passport.PassportWebEmailPasswordResetGet({ ...params, code: '' });
    // After updating the password, the current login state is invalid, reset the store
    resetUserStore();
  },

  /**
   * Change password with old password verification
   * This API enforces password policy (complexity, history)
   */
  changePassword: async (params: {
    old_password: string;
    new_password: string;
  }) => {
    await passport.UserChangePassword(params);
    // After changing the password, the current login state is invalid, reset the store
    resetUserStore();
  },

  updateUserProfile: (params: UserUpdateProfileRequest) =>
    passport.UserUpdateProfile(params),
};
